/** HR helpdesk & case management — module 19. */

export type TicketStatus =
  | 'NEW' | 'ASSIGNED' | 'IN_PROGRESS' | 'WAITING' | 'RESOLVED' | 'CLOSED' | 'CANCELLED'
export type TicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
export type KbStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type BreachKind = 'RESPONSE' | 'RESOLUTION'

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'Being worked on',
  WAITING: 'Waiting on the requester',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
}

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
}

export const KB_STATUS_LABELS: Record<KbStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Withdrawn',
}

export interface TicketCategoryView {
  id: string
  legalEntityId: string | null
  parentId: string | null
  code: string
  name: string
  description: string | null
  defaultAssigneeRoleId: string | null
  defaultAssigneeRoleCode: string | null
  /** Null means tickets here have no response due time at all. */
  responseMinutes: number | null
  /** Null means tickets here have no resolution due time at all. */
  resolutionMinutes: number | null
  escalateAfterMinutes: number | null
  escalateToRoleId: string | null
  escalateToRoleCode: string | null
  confidential: boolean
  requiresApproval: boolean
  active: boolean
  openTickets: number
  slaNote: string
}

export interface TicketView {
  id: string
  legalEntityId: string | null
  categoryId: string
  categoryName: string | null
  ticketNo: string
  subject: string
  description: string | null
  raisedByEmployeeId: string | null
  raisedByName: string | null
  aboutEmployeeId: string | null
  aboutName: string | null
  priority: string
  status: string
  assigneeUserId: string | null
  assigneeName: string | null
  assetId: string | null
  assetTag: string | null
  libraryDocumentId: string | null
  responseDueAt: string | null
  resolutionDueAt: string | null
  firstResponseAt: string | null
  resolvedAt: string | null
  closedAt: string | null
  resolution: string | null
  reopenedCount: number
  confidential: boolean
  createdAt: string | null
  commentCount: number
  satisfactionScore: number | null
  responseBreached: boolean
  resolutionBreached: boolean
  slaNote: string
  confidentialityNote: string | null
}

export interface TicketCommentView {
  id: string
  ticketId: string
  authorUserId: string | null
  authorName: string | null
  body: string
  internal: boolean
  createdAt: string | null
}

export interface TicketEscalationView {
  id: string
  ticketId: string
  breached: string
  escalatedAt: string
  escalatedToRoleId: string | null
  escalatedToRoleCode: string | null
  recipientCount: number
  note: string | null
}

export interface TicketSatisfactionView {
  id: string
  ticketId: string
  score: number
  comment: string | null
  submittedAt: string
}

export interface KbArticleView {
  id: string
  legalEntityId: string | null
  categoryId: string | null
  categoryName: string | null
  slug: string
  title: string
  summary: string | null
  body: string
  tags: string[]
  status: string
  publishedAt: string | null
  authorEmployeeId: string | null
  authorName: string | null
  helpfulYes: number
  helpfulNo: number
  viewCount: number
  feedbackNote: string
}

export interface HelpdeskSummaryView {
  open: number
  unassigned: number
  breachedResponse: number
  breachedResolution: number
  resolved: number
  closed: number
  /** Null when nobody has rated anything — not a zero score. */
  averageSatisfaction: number | null
  rated: number
  reopened: number
  withoutSla: number
  note: string
}
